import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEdmDocumentationCategoryCreateMutation } from 'graphql-common';
import { FormRef } from '@lib/components/ReactHookForm/types';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { Values } from '@lib/interfaces/form';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import getPreparedDataFromValues from '../../DocumentCategories/utils/getPreparedDataFromValues';

type UseAddCategoryModalArgs = {
  formRef: React.RefObject<FormRef | undefined>;
};

export default function useAddCategoryModal({
  formRef,
}: UseAddCategoryModalArgs) {
  const { t } = useTranslation();
  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryCacheKey, setCategoryCacheKey] = useState(0);
  const openAddCategoryModal = useCallback(
    () => setCategoryModalOpen(true),
    [],
  );
  const closeAddCategoryModal = useCallback(
    () => setCategoryModalOpen(false),
    [],
  );

  const [categoryCreateMutation, categoryCreateMutationResult] =
    useEdmDocumentationCategoryCreateMutation({
      onCompleted: (response) => {
        const category = response.data;
        if (category?.id && formRef.current?.setValue) {
          formRef.current.setValue(
            DocumentFormFieldNames.Category,
            {
              value: category.id,
              label: category.name,
              color: category.color,
            },
            { shouldDirty: true, shouldValidate: true },
          );
          setCategoryCacheKey((k) => k + 1);
        }
        closeAddCategoryModal();
        toast({ content: t('create-category-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onSubmitAddCategory = useCallback(
    (values: Values) => {
      const attributes = getPreparedDataFromValues(values);
      categoryCreateMutation({ variables: { attributes } });
    },
    [categoryCreateMutation],
  );

  return {
    isCategoryModalOpen,
    categoryCacheKey,
    openAddCategoryModal,
    closeAddCategoryModal,
    onSubmitAddCategory,
    categoryCreateMutationResult,
  };
}
